import { diff } from "deep-object-diff";
import { ScriptType } from "@/enums/campaigns";
import { PhoneScript } from "@/types/campaigns";
import { InboundRule, InboundRulesSimple, UseCase, UseCaseCreate } from "@/types/scripts";
import {
  getSuccessfulUseCaseId,
  isGailApiErrorPayload,
  reportUseCaseSaveFailed,
} from "@/utils/common/gailApiErrors";
import { GAIL_API } from "@/utils/common/constants/routes";
import { GetDynamicContentBE } from "@/utils/common/regex";
import {
  currentRuleHasConflicts,
  type InboundRuleConflictOptions,
} from "@/utils/inbound/operatingTimes";
import { findAnyAnyRuleForPhone } from "@/utils/inbound/scriptStatus";
import { sanitizeGreetingFields } from "@/utils/scripts/greetingFields";
import { sanitizeFaqsForStorage } from "@/utils/review/scrubFaqVariables";
import { sanitizeVoiceSettings } from "@/utils/scripts/voiceSettings";
import { sanitizeApplicableOutcomesOnScript } from "@/utils/orgOutcomes/sanitizeApplicableOutcomesOnScript";
import { SCRIPT_API_VERSION } from "@/enums/profile";
import { getScriptApiUrl, type ScriptApiVersion } from "@/utils/scriptVersioning/api";
import {
  type EmbeddedUseCasesBody,
  withEmbeddedUseCasesBody,
} from "@/utils/scripts/embeddedUseCases";

/**
 * Single save-boundary sanitizer for phone scripts. Strips voice-settings
 * fields that don't apply to the current provider and clears greeting fields
 * from modes the user isn't using. Inbound, outbound, onboarding, restore,
 * and eval persist should reuse this rather than assembling their own
 * association-vs-embedded payload.
 */
export type SanitizePhoneScriptOptions = {
  isV2?: boolean;
  modifiedUseCases?: Map<string, UseCase>;
};

const sanitizeForSave = async (
  script: PhoneScript,
  options?: SanitizePhoneScriptOptions
): Promise<PhoneScript | EmbeddedUseCasesBody<PhoneScript>> => {
  const isV2 = options?.isV2 ?? false;
  const base = sanitizeGreetingFields({
    ...sanitizeVoiceSettings(script),
    faqs: sanitizeFaqsForStorage(script.faqs),
  });
  const sanitized = await sanitizeApplicableOutcomesOnScript(base);
  if (!isV2) {
    return { ...sanitized, useCases: [] };
  }
  return withEmbeddedUseCasesBody(sanitized, script, options?.modifiedUseCases);
};

/** Shared save-boundary sanitizer for phone scripts (draft save + restore). */
export const sanitizePhoneScriptForSave = sanitizeForSave;

export type SaveResult<T> =
  | { success: true; data: T }
  | {
      success: false;
      error: "use_cases" | "script" | "rules" | "rules_conflict";
      failedUseCaseId?: string;
      failedUseCaseTitle?: string;
      apiErrorPayload?: unknown;
    };

export type ChangeDetectionResult = {
  scriptChanged: boolean;
  rulesChanged: boolean;
  useCasesChanged: boolean;
  hasAnyChanges: boolean;
};

export type ApiFunction = <TOutput>(
  url: string,
  method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH",
  options?: {
    body?: unknown;
    alwaysReturn?: boolean;
    silent?: boolean;
  },
) => Promise<TOutput | null>;

/**
 * Detect what has changed between original and current state
 */
export const detectChanges = (
  originalScript: PhoneScript | null | undefined,
  currentScript: PhoneScript | null | undefined,
  originalRules: InboundRulesSimple | undefined,
  currentRules: InboundRulesSimple | undefined,
  modifiedUseCases: Map<string, UseCase>
): ChangeDetectionResult => {
  const scriptChanged = Object.keys(diff(originalScript ?? {}, currentScript ?? {})).length > 0;
  const rulesChanged = Object.keys(diff(originalRules ?? {}, currentRules ?? {})).length > 0;
  const useCasesChanged = modifiedUseCases.size > 0;

  return {
    scriptChanged,
    rulesChanged,
    useCasesChanged,
    hasAnyChanges: scriptChanged || rulesChanged || useCasesChanged,
  };
};

/**
 * Create the payload for a use case
 */
export const createUseCasePayload = (useCase: UseCase): UseCaseCreate => ({
  title: useCase.title,
  description: GetDynamicContentBE(useCase.description),
  actions: (useCase.actions || []).map((a) => ({
    description: GetDynamicContentBE(a.description),
    ...(a.type ? { type: a.type } : {}),
    ...(a.textScriptId ? { textScriptId: a.textScriptId } : {}),
  })),
});

/**
 * Determine if a use case is new (temporary ID) vs existing
 */
export const isNewUseCase = (useCase: UseCase): boolean =>
  useCase.id.startsWith("usecase_") ||
  useCase.id.startsWith("action_") ||
  useCase.id.startsWith("temp_");

/**
 * Get the API URL for saving a use case
 */
export const getUseCaseUrl = (useCase: UseCase): string =>
  isNewUseCase(useCase) ? `${GAIL_API}/v1/use_cases` : `${GAIL_API}/v1/use_cases/${useCase.id}`;

/**
 * Get the HTTP method for saving a use case
 */
export const getUseCaseMethod = (useCase: UseCase): "POST" | "PUT" =>
  isNewUseCase(useCase) ? "POST" : "PUT";

/**
 * Save all modified use cases and return updated script with new IDs
 */
export const saveUseCases = async (
  script: PhoneScript,
  modifiedUseCases: Map<string, UseCase>,
  apiCall: ApiFunction
): Promise<SaveResult<PhoneScript>> => {
  if (modifiedUseCases.size === 0) {
    return { success: true, data: script };
  }

  const useCaseIdMap = new Map<string, string>();
  const useCases = Array.from(modifiedUseCases.values());

  const useCaseSavePromises = useCases.map(async (useCase) => {
    const isNew = isNewUseCase(useCase);
    const payload = createUseCasePayload(useCase);

    const response = await apiCall<UseCase>(
      getUseCaseUrl(useCase),
      getUseCaseMethod(useCase),
      { body: payload, alwaysReturn: true, silent: true },
    );

    const responseId = getSuccessfulUseCaseId(response);
    const failed = responseId === null;

    if (!failed && isNew) {
      useCaseIdMap.set(useCase.id, responseId);
    }

    return {
      useCase,
      response,
      failed,
      apiErrorPayload: failed ? response : undefined,
    };
  });

  const results = await Promise.all(useCaseSavePromises);
  const failed = results.find((result) => result.failed);

  if (failed) {
    reportUseCaseSaveFailed({
      failedUseCaseId: failed.useCase.id,
      failedUseCaseTitle: failed.useCase.title || undefined,
    });
    return {
      success: false,
      error: "use_cases",
      failedUseCaseId: failed.useCase.id,
      failedUseCaseTitle: failed.useCase.title || undefined,
      apiErrorPayload: failed.apiErrorPayload,
    };
  }

  const updatedAssociations = (script.useCaseAssociations || []).map((assoc) => ({
    ...assoc,
    id: useCaseIdMap.get(assoc.id) || assoc.id,
  }));

  return {
    success: true,
    data: {
      ...script,
      useCaseAssociations: updatedAssociations,
    },
  };
};

/**
 * Update rules to replace temporary "new" scriptId with actual ID
 */
export const updateRulesWithScriptId = (
  rules: InboundRulesSimple,
  newScriptId: string
): InboundRulesSimple => ({
  ...rules,
  defaultScriptId: rules.defaultScriptId === "new" ? newScriptId : rules.defaultScriptId,
  rules: rules.rules.map((rule) => ({
    ...rule,
    scriptId: rule.scriptId === "new" ? newScriptId : rule.scriptId,
  })),
});

type InboundRuleUpdate = {
  rule: InboundRule;
  removeScriptIds?: ReadonlyArray<string>;
};

/**
 * Apply a single rule create/update (and optional removals) onto a base rules
 * document without disturbing unrelated scripts or phone numbers.
 */
export const applyInboundRuleUpdate = (
  baseRules: InboundRulesSimple,
  update: InboundRuleUpdate
): InboundRulesSimple => {
  const removeScriptIds = new Set(update.removeScriptIds ?? []);
  let nextRules = baseRules.rules.filter((rule) => !removeScriptIds.has(rule.scriptId));

  const existingRuleIndex = nextRules.findIndex((rule) => rule.scriptId === update.rule.scriptId);
  if (existingRuleIndex >= 0) {
    nextRules = nextRules.map((rule, index) =>
      index === existingRuleIndex ? update.rule : rule
    );
  } else {
    nextRules = [...nextRules, update.rule];
  }

  return {
    ...baseRules,
    rules: nextRules,
  };
};

/**
 * Make this script the per-phone Default (any/any) on `phoneNumber`.
 * Does not change org-wide `defaultScriptId`. The previous any/any on that
 * phone is removed (orphaned).
 */
export const applyPerPhoneDefaultRule = (
  baseRules: InboundRulesSimple,
  scriptId: string,
  phoneNumber: string,
  defaultPhoneNumber?: string
): InboundRulesSimple => {
  const existing = findAnyAnyRuleForPhone(
    baseRules.rules,
    phoneNumber,
    defaultPhoneNumber,
    scriptId
  );
  return applyInboundRuleUpdate(baseRules, {
    rule: {scriptId, timeFrame: "any", hours: "any", phoneNumber},
    removeScriptIds: existing ? [existing.scriptId] : undefined,
  });
};

/**
 * Merge only the current script's operating rule onto the latest rules payload.
 */
export const mergeInboundRuleForScript = (
  baseRules: InboundRulesSimple,
  scriptId: string,
  currentRules: InboundRulesSimple
): InboundRulesSimple => {
  const currentRule = currentRules.rules.find((rule) => rule.scriptId === scriptId);

  if (!currentRule) {
    return {
      ...baseRules,
      rules: baseRules.rules.filter((rule) => rule.scriptId !== scriptId),
    };
  }

  return applyInboundRuleUpdate(baseRules, { rule: currentRule });
};

const loadLatestInboundRules = async (
  apiCall: ApiFunction,
  fallbackRules?: InboundRulesSimple
): Promise<InboundRulesSimple | undefined> => {
  const latestRules = await apiCall<InboundRulesSimple>("/api/inbound-rules", "GET");
  if (latestRules?.rules) {
    return latestRules;
  }
  return fallbackRules;
};

export type SaveNewScriptResult =
  | { success: true; scriptId: string; data?: PhoneScript }
  | {
      success: false;
      error: "use_cases" | "script" | "rules" | "rules_conflict";
      failedUseCaseId?: string;
      failedUseCaseTitle?: string;
      apiErrorPayload?: unknown;
    };

export type ValidateInboundRulesOptions = InboundRuleConflictOptions & {
  scriptId?: string;
};

export const validateInboundRulesForSave = (
  rules: InboundRulesSimple | undefined,
  conflictOptions: ValidateInboundRulesOptions
): boolean => {
  if (!rules || !conflictOptions.scriptId) return true;

  const rule = rules.rules.find((r) => r.scriptId === conflictOptions.scriptId);
  if (!rule) return true;

  return !currentRuleHasConflicts(rule, rules.rules, conflictOptions);
};

/**
 * Save a new script (script + optional rules)
 */
export const saveNewScript = async (
  script: PhoneScript,
  rules: InboundRulesSimple | undefined,
  modifiedUseCases: Map<string, UseCase>,
  apiCall: ApiFunction,
  conflictOptions?: InboundRuleConflictOptions & { scriptId?: string },
  apiVersion: ScriptApiVersion = SCRIPT_API_VERSION.V1
): Promise<SaveNewScriptResult> => {
  const isV2 = apiVersion === SCRIPT_API_VERSION.V2;
  let scriptToCreate = script;

  if (!isV2) {
    const useCasesResult = await saveUseCases(script, modifiedUseCases, apiCall);

    if (!useCasesResult.success) {
      return {
        success: false,
        error: "use_cases",
        failedUseCaseId: useCasesResult.failedUseCaseId,
        failedUseCaseTitle: useCasesResult.failedUseCaseTitle,
        apiErrorPayload: useCasesResult.apiErrorPayload,
      };
    }

    scriptToCreate = useCasesResult.data;
  }

  const scriptResponse = await apiCall<PhoneScript>(
    getScriptApiUrl("", ScriptType.Phone, apiVersion),
    "POST",
    {
      body: await sanitizeForSave(
        {
          ...scriptToCreate,
          id: "",
        },
        { isV2, modifiedUseCases }
      ),
    },
  );

  if (!scriptResponse) {
    return { success: false, error: "script" };
  }

  if (rules) {
    const newScriptRule = rules.rules.find((rule) => rule.scriptId === "new");

    if (newScriptRule) {
      if (
        conflictOptions &&
        !validateInboundRulesForSave(rules, conflictOptions)
      ) {
        return { success: false, error: "rules_conflict" };
      }

      const latestRules =
        (await loadLatestInboundRules(apiCall, rules)) ?? rules;
      const rulesToSave = mergeInboundRuleForScript(
        latestRules,
        scriptResponse.id,
        {
          defaultScriptId: latestRules.defaultScriptId,
          rules: [{ ...newScriptRule, scriptId: scriptResponse.id }],
        }
      );

      const rulesResponse = await apiCall<InboundRulesSimple>(
        "/api/inbound-rules",
        "PUT",
        { body: rulesToSave },
      );

      if (!rulesResponse) {
        return { success: false, error: "rules" };
      }
    }
  }

  return { success: true, scriptId: scriptResponse.id, data: scriptResponse };
};

export type SaveExistingScriptResult =
  | { success: true; data: PhoneScript; noop?: true }
  | {
      success: false;
      error: "use_cases" | "script" | "rules" | "rules_conflict";
      failedUseCaseId?: string;
      failedUseCaseTitle?: string;
      apiErrorPayload?: unknown;
    };

/**
 * Save an existing script (only saves what has changed)
 */
export const saveExistingScript = async (
  script: PhoneScript,
  rules: InboundRulesSimple | undefined,
  modifiedUseCases: Map<string, UseCase>,
  originalScript: PhoneScript | null | undefined,
  originalRules: InboundRulesSimple | undefined,
  apiCall: ApiFunction,
  conflictOptions?: InboundRuleConflictOptions & { scriptId?: string },
  apiVersion: ScriptApiVersion = SCRIPT_API_VERSION.V1
): Promise<SaveExistingScriptResult> => {
  const changes = detectChanges(originalScript, script, originalRules, rules, modifiedUseCases);

  if (!changes.hasAnyChanges) {
    return { success: true, data: script, noop: true };
  }

  const isV2 = apiVersion === SCRIPT_API_VERSION.V2;
  let scriptToSave = script;
  if (changes.useCasesChanged && !isV2) {
    const useCasesResult = await saveUseCases(script, modifiedUseCases, apiCall);

    if (!useCasesResult.success) {
      return {
        success: false,
        error: "use_cases",
        failedUseCaseId: useCasesResult.failedUseCaseId,
        failedUseCaseTitle: useCasesResult.failedUseCaseTitle,
        apiErrorPayload: useCasesResult.apiErrorPayload,
      };
    }

    scriptToSave = useCasesResult.data;
  }

  const savePromises: Promise<unknown>[] = [];

  if (changes.scriptChanged || changes.useCasesChanged) {
    savePromises.push(
      apiCall<PhoneScript>(
        getScriptApiUrl(scriptToSave.id, ScriptType.Phone, apiVersion),
        "PUT",
        {
          body: await sanitizeForSave(scriptToSave, { isV2, modifiedUseCases }),
          alwaysReturn: true,
          silent: true,
        },
      )
    );
  }

  if (changes.rulesChanged && rules) {
    if (!conflictOptions?.scriptId) {
      return { success: false, error: "rules" };
    }

    if (!validateInboundRulesForSave(rules, conflictOptions)) {
      return { success: false, error: "rules_conflict" };
    }

    const latestRules =
      (await loadLatestInboundRules(apiCall, originalRules ?? rules)) ??
      originalRules ??
      rules;
    const rulesToSave = mergeInboundRuleForScript(
      latestRules,
      conflictOptions.scriptId,
      rules
    );

    savePromises.push(
      apiCall<InboundRulesSimple>("/api/inbound-rules", "PUT", { body: rulesToSave })
    );
  }

  const results = await Promise.all(savePromises);

  if (results.includes(null) || results.some(isGailApiErrorPayload)) {
    return { success: false, error: "script" };
  }

  // v2 PUT returns the updated script (incl. revision fields); prefer it when present.
  const scriptResult = results.find(
    (result): result is PhoneScript =>
      !!result &&
      typeof result === "object" &&
      "id" in result &&
      typeof (result as PhoneScript).id === "string" &&
      !isGailApiErrorPayload(result)
  );

  if (scriptResult && "revision" in scriptResult) {
    return { success: true, data: { ...scriptToSave, ...scriptResult } };
  }

  return { success: true, data: scriptToSave };
};
